import React, { createContext, useContext, useState } from 'react';
import { User, Tenant } from '../types';
import { MOCK_USERS, MOCK_TENANTS } from '../mockData';

interface AuthContextType {
  currentUser: User | null;
  currentTenant: Tenant | null;
  availableUsers: User[];
  availableTenants: Tenant[];
  isAuthenticated: boolean;
  loginWithGoogle: (email?: string) => Promise<void>;
  switchUser: (userId: string) => void;
  switchTenant: (slug: string) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(MOCK_USERS[0]);
  const [currentTenant, setCurrentTenant] = useState<Tenant | null>(MOCK_TENANTS[0]);

  const loginWithGoogle = async (email?: string) => {
    const target = email
      ? MOCK_USERS.find((u) => u.email === email) || MOCK_USERS[0]
      : MOCK_USERS[0];
    setCurrentUser(target);
    setCurrentTenant(MOCK_TENANTS[0]);
  };

  const switchUser = (userId: string) => {
    const target = MOCK_USERS.find((u) => u.id === userId);
    if (target) {
      setCurrentUser(target);
    }
  };

  const switchTenant = (slug: string) => {
    const target = MOCK_TENANTS.find((t) => t.slug === slug);
    if (target) {
      setCurrentTenant(target);
    }
  };

  const logout = () => {
    setCurrentUser(null);
  };

  return (
    <AuthContext.Provider
      value={{
        currentUser,
        currentTenant,
        availableUsers: MOCK_USERS,
        availableTenants: MOCK_TENANTS,
        isAuthenticated: !!currentUser,
        loginWithGoogle,
        switchUser,
        switchTenant,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
